/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { ResizablePanel } from './ResizablePanel';

export interface ResizablePanelWrapperProps {
  name: string;
  children: React.ReactNode;
  isDisplayed: boolean;
  /**
   * Optional. Only used by redux-aware panel components (e.g.
   * SoundingsPanelConnect) to key external state. Standalone usage ignores it.
   */
  // eslint-disable-next-line react/no-unused-prop-types
  panelId?: string;
  /**
   * Optional. Only used by redux-aware panel components to key external state
   * by parent view. Standalone usage ignores it.
   */
  // eslint-disable-next-line react/no-unused-prop-types
  viewId?: string;
  headerAction?: React.ReactElement;
  handleButtonComponent?: React.ReactElement;
  maxWidth?: number;
  minWidth?: number;
  onOpenChange?: (isOpen: boolean) => void;
  onResizeStart?: () => void;
  onResizeEnd?: () => void;
  direction?: 'horizontal' | 'vertical';
}

export type ResizablePanelComponent = React.ForwardRefExoticComponent<
  ResizablePanelWrapperProps & React.RefAttributes<HTMLDivElement>
>;

export const ResizablePanelStandalone = React.forwardRef<
  HTMLDivElement,
  ResizablePanelWrapperProps
>(
  (
    {
      name,
      children,
      headerAction,
      isDisplayed,
      maxWidth,
      minWidth,
      handleButtonComponent,
      onOpenChange,
      onResizeStart,
      onResizeEnd,
      direction,
    },
    ref,
  ): React.ReactElement => {
    const [isOpen, setIsOpen] = React.useState(true);
    const [customWidth, setCustomWidth] = React.useState<number | undefined>(
      undefined,
    );

    const handleSetIsOpen = React.useCallback(
      (open: boolean) => {
        setIsOpen(open);
        onOpenChange?.(open);
      },
      [onOpenChange],
    );

    return (
      <ResizablePanel
        customWidth={customWidth}
        direction={direction}
        handleButtonComponent={handleButtonComponent}
        headerAction={headerAction}
        isDisplayed={isDisplayed}
        isOpen={isOpen}
        maxWidth={maxWidth}
        minWidth={minWidth}
        name={name}
        onResizeEnd={onResizeEnd}
        onResizeStart={onResizeStart}
        onSetCustomWidth={setCustomWidth}
        onSetIsOpen={handleSetIsOpen}
        ref={ref}
      >
        {children}
      </ResizablePanel>
    );
  },
);

ResizablePanelStandalone.displayName = 'ResizablePanelStandalone';
